import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { gqlClient } from '@/lib';
import type {
  CreateChildExperienceInput,
  ExperienceChildConfigName,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  CreateChildExperienceMutation,
  CreateChildExperienceMutationVariables,
} from './useCreateChildExperience.generated';

const document = gql`
  mutation CreateChildExperience($input: CreateChildExperienceInput!) {
    createChildExperience(input: $input) {
      id
    }
  }
`;

export const useCreateChildExperience = (parentExperienceId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    CreateChildExperienceMutation,
    QueryError,
    CreateChildExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        CreateChildExperienceMutation,
        CreateChildExperienceMutationVariables
      >(document, variables),
    // Return the invalidation promise so the mutation stays pending until the
    // parent refetch settles. The caller's onSuccess (which selects the new
    // child) then runs only after experience.children includes it — avoiding a
    // flash where useActiveView falls back to the default child.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(parentExperienceId),
      }),
  });

  const createChildExperience = useCallback(
    (
      config: ExperienceChildConfigName,
      options?: MutateOptions<
        CreateChildExperienceMutation,
        QueryError,
        CreateChildExperienceMutationVariables
      >
    ) => {
      const input: CreateChildExperienceInput = {
        experienceId: parentExperienceId,
        config,
      };
      return mutate({ input }, options);
    },
    [mutate, parentExperienceId]
  );

  return { createChildExperience, isPending };
};
